import * as THREE from 'three'

export interface WaterResult {
  mesh: THREE.Mesh
  update(t: number, camera: THREE.Camera, sunDir: THREE.Vector3): void
  dispose(): void
}

const VERT = /* glsl */ `
  varying vec3 vSvetPoz;
  void main() {
    vec4 sp = modelMatrix * vec4( position, 1.0 );
    vSvetPoz = sp.xyz;
    gl_Position = projectionMatrix * viewMatrix * sp;
  }
`

const FRAG = /* glsl */ `
  precision highp float;

  varying vec3 vSvetPoz;

  uniform sampler2D uDubina;
  uniform float uVreme;
  uniform float uMapa;       // širina terena u svetskim jedinicama
  uniform vec3 uKamera;
  uniform vec3 uSunce;       // normalizovan pravac ka suncu
  uniform vec3 uSunceBoja;
  uniform vec3 uNebo;
  uniform vec3 uPlitko;
  uniform vec3 uDuboko;
  uniform vec3 uMagla;
  uniform float uMaglaOd;
  uniform float uMaglaDo;

  // Tri ukrštena talasa daju nepravilnu, ali jeftinu normalu.
  vec3 normalaTalasa( vec2 p, float t, float jacina ) {
    vec2 n = vec2( 0.0 );
    vec2 sm1 = normalize( vec2( 1.0, 0.35 ) );
    vec2 sm2 = normalize( vec2( -0.45, 1.0 ) );
    vec2 sm3 = normalize( vec2( 0.8, -0.7 ) );
    n += sm1 * cos( dot( p, sm1 ) * 0.045 + t * 1.10 ) * 0.55;
    n += sm2 * cos( dot( p, sm2 ) * 0.085 + t * 1.55 ) * 0.30;
    n += sm3 * cos( dot( p, sm3 ) * 0.210 + t * 2.40 ) * 0.12;
    return normalize( vec3( -n.x * 0.085 * jacina, 1.0, -n.y * 0.085 * jacina ) );
  }

  void main() {
    vec2 uv = ( vSvetPoz.xz + uMapa * 0.5 ) / uMapa;
    vec2 uvc = clamp( uv, 0.0, 1.0 );
    float dubina = texture2D( uDubina, uvc ).r;
    // Van granica mape voda je uvek otvoreno more.
    float vanMape = max( abs( uv.x - 0.5 ), abs( uv.y - 0.5 ) );
    dubina = mix( dubina, 1.0, smoothstep( 0.44, 0.5, vanMape ) );

    float udaljenost = length( uKamera - vSvetPoz );
    // Talasi se gase u daljini, inače daju moiré pruge do horizonta.
    float daljina = 1.0 - smoothstep( uMapa * 0.35, uMapa * 1.3, udaljenost );
    float jacina = mix( 0.3, 1.0, dubina ) * daljina;

    vec3 pogled = normalize( uKamera - vSvetPoz );
    vec3 N = normalaTalasa( vSvetPoz.xz, uVreme, jacina );

    float fresnel = pow( 1.0 - max( dot( N, pogled ), 0.0 ), 5.0 );
    fresnel = clamp( 0.02 + 0.98 * fresnel, 0.0, 1.0 );

    vec3 boja = mix( uPlitko, uDuboko, smoothstep( 0.05, 0.75, dubina ) );
    boja = mix( boja, uNebo, fresnel * 0.8 );

    // Sunčev odsjaj, prigušen u daljini da ne svetluca po pikselima.
    vec3 H = normalize( uSunce + pogled );
    float spek = pow( max( dot( N, H ), 0.0 ), 180.0 );
    boja += uSunceBoja * spek * 1.1 * daljina;

    // Pena uz obalu — pomerana talasima, da linija ne bude mrtva.
    float pomeraj = sin( vSvetPoz.x * 0.09 + uVreme * 1.4 ) * 0.5
                  + sin( vSvetPoz.z * 0.11 - uVreme * 1.1 ) * 0.5;
    float prag = 0.05 + pomeraj * 0.02;
    float pena = smoothstep( prag, 0.004, dubina ) * smoothstep( 0.0, 0.005, dubina );
    boja = mix( boja, vec3( 0.9, 0.95, 0.97 ), pena * 0.8 * daljina );

    // Plitko je providno da se vidi peščano dno; duboko je potpuno neprozirno,
    // čime nestaje šav na ivici mreže terena.
    float alfa = mix( 0.40, 1.0, smoothstep( 0.0, 0.42, dubina ) );
    alfa = max( alfa, pena * 0.85 );

    // Magla se računa ručno — ShaderMaterial ne dobija three-ov fog.
    float magla = smoothstep( uMaglaOd, uMaglaDo, udaljenost );
    boja = mix( boja, uMagla, magla );
    alfa = mix( alfa, 1.0, magla );

    gl_FragColor = vec4( boja, alfa );
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`

export function buildWater(
  mapSize: number,
  depthTexture: THREE.DataTexture,
  fog: THREE.Fog,
): WaterResult {
  const geom = new THREE.PlaneGeometry(mapSize * 8, mapSize * 8, 1, 1)
  geom.rotateX(-Math.PI / 2)

  const mat = new THREE.ShaderMaterial({
    vertexShader: VERT,
    fragmentShader: FRAG,
    transparent: true,
    depthWrite: false,
    uniforms: {
      uDubina: { value: depthTexture },
      uVreme: { value: 0 },
      uMapa: { value: mapSize },
      uKamera: { value: new THREE.Vector3() },
      uSunce: { value: new THREE.Vector3(0, 1, 0) },
      uSunceBoja: { value: new THREE.Color('#ffe9c2') },
      uNebo: { value: fog.color },
      uPlitko: { value: new THREE.Color('#2f7f86') },
      uDuboko: { value: new THREE.Color('#0d2b3c') },
      uMagla: { value: fog.color },
      uMaglaOd: { value: fog.near },
      uMaglaDo: { value: fog.far },
    },
  })

  const mesh = new THREE.Mesh(geom, mat)
  mesh.position.y = 0
  mesh.renderOrder = 2
  mesh.name = 'voda'

  return {
    mesh,
    update(t, camera, sunDir) {
      mat.uniforms.uVreme.value = t
      mat.uniforms.uKamera.value.copy(camera.position)
      mat.uniforms.uSunce.value.copy(sunDir)
    },
    dispose() {
      geom.dispose()
      mat.dispose()
      depthTexture.dispose()
    },
  }
}
